import {
  autoUpdate,
  flip,
  offset,
  Placement,
  shift,
  size,
  useFloating,
} from '@floating-ui/react';
import React, { forwardRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useClickOutside } from 'shared/hooks/use-click-outside';
import { useForwardedRef } from 'shared/hooks/use-forwarded-ref';
import { useElectron } from 'shared/hooks/use-is-mac';
import { useKeysDown } from 'shared/hooks/use-keys-down';
import { InteractionType } from 'shared/types/data-type';

import * as Styled from './popup-menu.style';

export type PopupMenuProps = {
  children: React.ReactNode;
  referenceElement?: React.RefObject<HTMLElement>;
  location?: { top: number; left: number };
  position?: Placement;
  fallbackPositions?: Placement[];
  preventReferenceElementOverlap?: boolean;
  hasParentWidth?: boolean;
  onOutside?: () => void;
  className?: string;
  interactionType?: InteractionType;
  noBackdrop?: boolean;
  width?: number;
  ignoreSelector?: string;
};

const fallbackPlacements: Placement[] = [
  'right-end',
  'right-start',
  'bottom-end',
  'bottom-start',
  'left-start',
  'left-end',
  'top-end',
  'top-start',
];

export const PopupMenu = forwardRef<HTMLDivElement, PopupMenuProps>(
  (
    {
      children,
      referenceElement,
      location,
      position = 'bottom-start',
      fallbackPositions = fallbackPlacements,
      preventReferenceElementOverlap,
      hasParentWidth,
      onOutside,
      className,
      interactionType,
      noBackdrop,
      width,
      ignoreSelector,
    },
    ref,
  ) => {
    const menuRef = useForwardedRef(ref);
    const { isMac } = useElectron();

    const { refs, floatingStyles } = useFloating({
      elements: {
        reference: referenceElement?.current,
      },
      placement: position,
      whileElementsMounted: autoUpdate,
      strategy: 'fixed',
      middleware: [
        offset({ mainAxis: 8 }),
        shift({
          padding: isMac
            ? {
                top: Styled.positioningOffset * 2,
                bottom: Styled.positioningOffset * 2,
                left: Styled.positioningOffset,
                right: Styled.positioningOffset,
              }
            : Styled.positioningOffset,
          crossAxis: !preventReferenceElementOverlap,
        }),
        flip({
          fallbackPlacements: fallbackPositions,
        }),
        hasParentWidth
          ? size({
              apply: ({ rects, elements }) => {
                elements.floating.style.width = `${rects.reference.width}px`;
              },
            })
          : undefined,
      ],
    });

    const clickOutside = (e: React.MouseEvent | MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();
      onOutside?.();
    };
    const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

    useKeysDown(
      ['Escape'],
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        onOutside?.();
      },
      { ignoreInputFields: false },
    );

    useEffect(() => {
      if (location) {
        refs.setPositionReference({
          getBoundingClientRect: () => ({
            width: 0,
            height: 0,
            x: location.left,
            y: location.top,
            top: location.top,
            right: location.left,
            bottom: location.top,
            left: location.left,
          }),
        });
      }
    }, [location, refs]);

    useEffect(() => {
      refs.setFloating(menuRef.current);
    }, [menuRef, refs]);

    useClickOutside(
      menuRef,
      noBackdrop ? clickOutside : undefined,
      ignoreSelector,
    );

    return createPortal(
      <>
        {!noBackdrop && (
          <Styled.Backdrop
            onMouseDown={clickOutside}
            onContextMenu={clickOutside}
          />
        )}
        <Styled.Container
          $width={width}
          className={className}
          style={floatingStyles}
          ref={menuRef}
          $isMac={isMac}
          data-no-dnd="true"
          data-interaction-for-type={interactionType}
          onClick={stopPropagation}
        >
          {children}
        </Styled.Container>
      </>,
      document.body,
    );
  },
);
